import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, MapPin, Plus, X, ChevronLeft, ChevronRight, User } from "lucide-react";
import api from "@/lib/api";
import RipOutFormDialog from "@/components/dashboard/RipOutFormDialog";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
    return d.toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", ...(iso.includes("T") ? { hour: "numeric", minute: "2-digit" } : {}) });
  } catch { return iso; }
};

/**
 * Rip-outs sub-tab (staff only — admins/owners/managers). Full company log
 * of rip-outs reported from the field, with photos. Everyone can submit
 * via the "Log rip-out" button (employees use the Requests tab card).
 */
const RipOutsTab = () => {
  const [items, setItems] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [viewer, setViewer] = useState(null); // { item, idx, url }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rip-outs");
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const showPhoto = async (item, idx) => {
    try {
      const { data } = await api.get(`/rip-outs/${item.ripout_id}/photos/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "image/jpeg" }));
      setViewer((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { item, idx, url };
      });
    } catch { /* photo missing */ }
  };
  const closeViewer = () => {
    if (viewer?.url) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  return (
    <div className="space-y-4" data-testid="ripouts-tab">
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-300">
              <Camera className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Rip-outs</h3>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                Work torn out by other trades — photo evidence for back-charges
              </p>
            </div>
          </div>
          <button
            onClick={() => setFormOpen(true)}
            data-testid="ripout-open-form"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-500"
          >
            <Plus className="h-3.5 w-3.5" /> Log rip-out
          </button>
        </div>

        {items === null ? (
          <div className="flex items-center justify-center py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="ripouts-empty">No rip-outs logged yet</p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="ripouts-list">
            {items.map((r) => (
              <li key={r.ripout_id} data-testid={`ripout-card-${r.ripout_id}`}
                className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 font-semibold text-white">
                    <User className="h-3 w-3 text-white/40" /> {r.employee_name}
                  </span>
                  <span className="font-mono text-white/60">{fmtWhen(r.occurred_at)}</span>
                  {r.project_name && (
                    <span className="rounded-full border border-[#1a3fd1]/40 bg-[#1a3fd1]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[#a9baf5]">{r.project_name}</span>
                  )}
                  <span className="min-w-0 flex-1" />
                  <button
                    onClick={() => showPhoto(r, 0)}
                    data-testid={`ripout-view-${r.ripout_id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:bg-white/10"
                  >
                    <Camera className="h-3 w-3" /> {r.photo_count} photo{r.photo_count === 1 ? "" : "s"}
                  </button>
                </div>
                <p className="mt-1 flex items-center gap-1 text-xs text-white/60">
                  <MapPin className="h-3 w-3 shrink-0 text-white/35" /> <span className="truncate">{r.address}</span>
                </p>
                {r.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-white/70">{r.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {formOpen && (
        <RipOutFormDialog
          onClose={() => setFormOpen(false)}
          onSubmitted={load}
          testid="ripout-form"
        />
      )}

      {viewer && createPortal(
        <div className="fixed inset-0 z-[95] flex flex-col bg-black/90" data-testid="ripout-viewer">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold" data-testid="ripout-viewer-title">
                {viewer.item.project_name || viewer.item.address}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                {viewer.item.employee_name} · {fmtWhen(viewer.item.occurred_at)} · photo {viewer.idx + 1} of {viewer.item.photo_count}
              </p>
            </div>
            <button onClick={closeViewer} aria-label="Close" data-testid="ripout-viewer-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center p-3">
            <img src={viewer.url} alt="Rip-out evidence" data-testid="ripout-viewer-img" className="max-h-full max-w-full rounded object-contain" />
            {viewer.item.photo_count > 1 && (
              <>
                <button
                  onClick={() => showPhoto(viewer.item, (viewer.idx - 1 + viewer.item.photo_count) % viewer.item.photo_count)}
                  aria-label="Previous photo" data-testid="ripout-viewer-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/60 p-2 text-white/80 hover:bg-black/80"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => showPhoto(viewer.item, (viewer.idx + 1) % viewer.item.photo_count)}
                  aria-label="Next photo" data-testid="ripout-viewer-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/60 p-2 text-white/80 hover:bg-black/80"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default RipOutsTab;
